import React from "react";
import PropTypes from "prop-types";
import {
  Typography,
  useTheme,
  Box,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { Item } from "./Item";

const CardDashboardItemVerticalMulti = ({
  title,
  subTitle,
  content,
  isLoading = false,
}) => {
  const theme = useTheme();
  // Very narrow phones (<360px): stack the header so the title and release
  // date wrap cleanly instead of crowding each other; ≥360px is unchanged.
  const isNarrowPhone = useMediaQuery("(max-width:359.95px)");

  return (
    <Item sx={{ padding: "1.5rem", margin: 0 }}>
      {isLoading ? (
        // Skeleton loading state
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Skeleton
              variant="text"
              width="40%"
              height={32}
              animation="wave"
              sx={{ backgroundColor: theme.palette.background.default }}
            />
            <Skeleton
              variant="text"
              width="30%"
              height={32}
              animation="wave"
              sx={{ backgroundColor: theme.palette.background.default }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Skeleton
                    variant="circular"
                    width={8}
                    height={8}
                    animation="wave"
                    sx={{
                      backgroundColor: theme.palette.background.default,
                      mr: 1,
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={24}
                    animation="wave"
                    sx={{ backgroundColor: theme.palette.background.default }}
                  />
                </Box>
              ))}
          </Box>
        </>
      ) : (
        // Actual content
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              ...(isNarrowPhone && {
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.5,
              }),
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                textAlign: "left",
                ...(isNarrowPhone && { width: "100%" }),
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.text3,
                textAlign: isNarrowPhone ? "left" : "right",
                ...(isNarrowPhone && { width: "100%" }),
              }}
            >
              {subTitle}
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            {content.map((item, index) => {
              const isSubItem = item.includes("- ");
              const cleanItem = item.replace(/^- /, "");

              return (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: isSubItem ? "6px" : "6px",
                      height: isSubItem ? "3px" : "6px",
                      minWidth: isSubItem ? "6px" : "6px",
                      minHeight: isSubItem ? "3px" : "6px",
                      borderRadius: isSubItem ? "0%" : "50%",
                      backgroundColor: theme.palette.primary.main,
                      mr: 1.5,
                      ml: isSubItem ? 2 : 0,
                      mt: "8px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.primary.text,
                      textAlign: "left",
                      flex: 1,
                    }}
                  >
                    {cleanItem}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Item>
  );
};

CardDashboardItemVerticalMulti.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  content: PropTypes.arrayOf(PropTypes.node).isRequired,
  isLoading: PropTypes.bool,
};

export default CardDashboardItemVerticalMulti;
